import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ChoiceButton, ExerciseCard, Stack } from '@fluent/ui';
import type { PhraseChoice as PhraseChoiceExercise } from '@fluent/types';
import { useAudio, type UseAudioState } from '../audio/audioPlayer.js';
import { BLANK, hasPromptAudio, phraseChoiceOptionLang, promptLines } from './phraseChoiceText.js';
import './PhraseChoice.css';

export interface PhraseChoiceProps {
  exercise: PhraseChoiceExercise;
  /** Display order for the options — the lesson player passes its seeded shuffle. */
  options?: readonly string[];
  selected: string | null;
  onSelectedChange: (next: string) => void;
  locked: boolean;
  /** Set after the user submits — drives the post-check reveal. */
  lastVerdict: 'exact' | 'wrong' | 'close' | null;
}

/** What the blank in a gap / complete prompt shows. */
type BlankState = 'empty' | 'filled' | 'exact' | 'fixed';

/**
 * PhraseChoice — the phrases course's test item (PHRASES.md § 5.1): one
 * schema, eight variants, every one "tap one of 3–4 options, then შემოწმება".
 * The variant decides the instruction (kicker), the prompt block and the
 * options' language:
 *
 *   meaning      big EN phrase + 🔊 (auto-plays once)   → Georgian meanings
 *   pick_phrase  the Georgian meaning, large           → EN phrases
 *   situation    a Georgian scene card                 → EN phrases
 *   gap          EN sentence / 2-line exchange, ONE ___ → EN phrases
 *   reply        a speech bubble someone says + 🔊      → EN replies
 *   paraphrase   a plain EN sentence in a quote card   → EN phrases
 *   complete     the phrase with one word missing      → single words
 *   odd_one_out  instruction only                      → EN phrases
 *
 * After the verdict: the blank holds the RIGHT answer (so the sentence on
 * screen is the one the clip says and `after_ka` translates — the wrong pick
 * stays red in the options), `after_ka` appears, and `audio_key` plays once.
 * That clip contains the answer, so it is sealed until then. `why_ka` is the
 * lesson's WhyCard (ResultDetail.whyFor), not part of this card.
 *
 * Presentational, like ChooseCorrect: the selection lives in the player.
 */
export function PhraseChoice({
  exercise,
  options,
  selected,
  onSelectedChange,
  locked,
  lastVerdict,
}: PhraseChoiceProps) {
  const { t } = useTranslation();
  const { variant } = exercise;
  const withPromptAudio = hasPromptAudio(exercise);
  const promptAudio = useAudio(withPromptAudio ? exercise.prompt_audio_key : null);
  const answerAudio = useAudio(exercise.audio_key);
  const promptPlayedRef = useRef<string | null>(null);
  const answerPlayedRef = useRef<string | null>(null);

  // The engine plays one clip at a time and does not tell a superseded clip's
  // owner — stop the other hook first so its button never stays "playing".
  const playPrompt = () => {
    answerAudio.stop();
    promptAudio.play();
  };
  const playAnswer = () => {
    promptAudio.stop();
    answerAudio.play();
  };

  // meaning / reply: the line is the question — say it once on arrival.
  useEffect(() => {
    if (!withPromptAudio || promptPlayedRef.current === exercise.id) return;
    promptPlayedRef.current = exercise.id;
    answerAudio.stop();
    promptAudio.play();
  }, [withPromptAudio, exercise.id, promptAudio, answerAudio]);

  // The answer clip, once, as the reward — never before the learner commits.
  useEffect(() => {
    if (lastVerdict == null || !exercise.audio_key || answerPlayedRef.current === exercise.id) return;
    answerPlayedRef.current = exercise.id;
    promptAudio.stop();
    answerAudio.play();
  }, [lastVerdict, exercise.id, exercise.audio_key, promptAudio, answerAudio]);

  const revealed = lastVerdict != null;
  const blank: { fill: string | null; state: BlankState } = !revealed
    ? { fill: selected, state: selected ? 'filled' : 'empty' }
    : { fill: exercise.answer, state: lastVerdict === 'wrong' ? 'fixed' : 'exact' };
  const optionLang = phraseChoiceOptionLang(variant);
  const shown = options ?? exercise.options;

  return (
    <ExerciseCard
      kicker={t(`lesson.phrase_choice.kicker.${variant}`)}
      className="fluent-phrase-choice"
      data-variant={variant}
      data-testid="phrase-choice"
    >
      <PromptBlock
        exercise={exercise}
        blank={blank}
        promptAudio={withPromptAudio ? promptAudio : null}
        onPromptAudio={() => (promptAudio.playing ? promptAudio.stop() : playPrompt())}
      />

      {revealed && (exercise.audio_key || exercise.after_ka) && (
        <div className="fluent-phrase-choice__reveal" data-testid="phrase-choice-reveal">
          {exercise.audio_key && (
            <AudioButton
              size="sm"
              className="fluent-phrase-choice__audio"
              playing={answerAudio.playing}
              onClick={() => (answerAudio.playing ? answerAudio.stop() : playAnswer())}
              label={t('lesson.phrase_choice.answer_audio_aria')}
              data-testid="phrase-choice-answer-audio"
            />
          )}
          {exercise.after_ka && (
            <p className="fluent-phrase-choice__after" lang="ka">
              {exercise.after_ka}
            </p>
          )}
        </div>
      )}

      <Stack gap={2} className="fluent-phrase-choice__options">
        {shown.map((opt, idx) => {
          const isSelected = selected === opt;
          // After submit, paint the answer green and the wrong pick red.
          const state = !revealed
            ? 'idle'
            : opt === exercise.answer
              ? 'correct'
              : isSelected
                ? 'wrong'
                : 'idle';
          return (
            <ChoiceButton
              key={opt}
              lang={optionLang}
              bullet={String.fromCharCode(65 + idx)}
              selected={isSelected}
              state={state}
              disabled={locked}
              onClick={() => onSelectedChange(opt)}
            >
              {opt}
            </ChoiceButton>
          );
        })}
      </Stack>
    </ExerciseCard>
  );
}

/** The variant's prompt: what the learner reads (and hears) before choosing. */
function PromptBlock({
  exercise,
  blank,
  promptAudio,
  onPromptAudio,
}: {
  exercise: PhraseChoiceExercise;
  blank: { fill: string | null; state: BlankState };
  /** Present for meaning / reply items that carry a prompt clip. */
  promptAudio: UseAudioState | null;
  onPromptAudio: () => void;
}) {
  const { t } = useTranslation();
  const audioButton = (size: 'sm' | 'md') =>
    promptAudio && (
      <AudioButton
        size={size}
        className="fluent-phrase-choice__audio"
        playing={promptAudio.playing}
        onClick={onPromptAudio}
        label={t('lesson.phrase_choice.prompt_audio_aria')}
        data-testid="phrase-choice-prompt-audio"
      />
    );

  switch (exercise.variant) {
    case 'meaning':
      return (
        <div className="fluent-phrase-choice__prompt">
          <p className="fluent-phrase-choice__phrase" lang="en">
            {exercise.prompt_en}
          </p>
          {audioButton('md')}
        </div>
      );
    case 'pick_phrase':
      return (
        <p className="fluent-phrase-choice__meaning" lang="ka">
          {exercise.prompt_ka}
        </p>
      );
    case 'situation':
      return (
        <div className="fluent-phrase-choice__scene">
          <p className="fluent-phrase-choice__scene-label">{t('lesson.phrase_choice.situation_label')}</p>
          <p className="fluent-phrase-choice__scene-text" lang="ka">
            {exercise.prompt_ka}
          </p>
        </div>
      );
    case 'gap':
      return <GapPrompt text={exercise.prompt_en ?? ''} blank={blank} />;
    case 'reply':
      return (
        <div
          className="fluent-phrase-choice__said"
          data-speaker={exercise.speaker ? exercise.speaker.toLowerCase() : undefined}
        >
          {exercise.speaker && <span className="fluent-phrase-choice__speaker">{exercise.speaker}</span>}
          <div className="fluent-phrase-choice__bubble">
            <p className="fluent-phrase-choice__bubble-text" lang="en">
              {exercise.prompt_en}
            </p>
            {audioButton('sm')}
          </div>
        </div>
      );
    case 'paraphrase':
      return (
        <div className="fluent-phrase-choice__quote">
          <p className="fluent-phrase-choice__quote-text" lang="en">
            {exercise.prompt_en}
          </p>
        </div>
      );
    case 'complete':
      return (
        <p className="fluent-phrase-choice__phrase" lang="en">
          <BlankText text={exercise.prompt_en ?? ''} blank={blank} />
        </p>
      );
    case 'odd_one_out':
      // An item-specific instruction (e.g. a register sort) replaces the
      // generic "three mean the same" line.
      return exercise.prompt_ka ? (
        <p className="fluent-phrase-choice__instruction" lang="ka">
          {exercise.prompt_ka}
        </p>
      ) : (
        <p className="fluent-phrase-choice__instruction">{t('lesson.phrase_choice.odd_one_out_hint')}</p>
      );
  }
}

/**
 * A gap prompt: one sentence, or a short exchange (`\n` between lines, each
 * may start "Name: ") drawn as two chat bubbles — the second answers the first.
 */
function GapPrompt({ text, blank }: { text: string; blank: { fill: string | null; state: BlankState } }) {
  const lines = promptLines(text);
  const only = lines.length === 1 ? lines[0] : undefined;
  if (only && !only.who) {
    return (
      <p className="fluent-phrase-choice__sentence" lang="en">
        <BlankText text={only.text} blank={blank} />
      </p>
    );
  }
  return (
    <div className="fluent-phrase-choice__exchange" lang="en">
      {lines.map((line, i) => (
        <div
          key={i}
          className="fluent-phrase-choice__line"
          data-side={i % 2 === 0 ? 'start' : 'end'}
        >
          {line.who && <span className="fluent-phrase-choice__who">{line.who}</span>}
          <p className="fluent-phrase-choice__line-text">
            <BlankText text={line.text} blank={blank} />
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * Text with its `___` drawn as a blank that fills live with the pick. The
 * blank is an inline box (not inline-block): a 4–6-word phrase wraps like
 * the words around it on a 320px phone instead of overflowing the card.
 */
function BlankText({ text, blank }: { text: string; blank: { fill: string | null; state: BlankState } }) {
  const { t } = useTranslation();
  const at = text.indexOf(BLANK);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="fluent-phrase-choice__blank" data-state={blank.state} data-testid="phrase-choice-blank">
        {blank.fill ?? (
          <>
            <span aria-hidden="true">{' '}</span>
            <span className="fluent-phrase-choice__sr" lang="ka">
              {t('lesson.phrase_choice.blank_aria')}
            </span>
          </>
        )}
      </span>
      {text.slice(at + BLANK.length)}
    </>
  );
}
